import clsx from 'clsx'
import React from 'react'
import { FlagAttribution } from './FlagAttribution'

const SWITCH_ICONS = [
  { value: false, icon: 'fa-solid fa-flag-swallowtail fa-sm', activeColor: '#E7785B', style: { marginTop: 2 } },
  { value: true, icon: 'fa-sharp fa-solid fa-circle-check', activeColor: '#4C7E77' },
]

const ToggleSwitch = ({ checked, onChange, readOnly, label }) => {
  if (readOnly) {
    const { icon, activeColor, style } = SWITCH_ICONS.find(({ value }) => value === checked) ?? SWITCH_ICONS[0]
    return (
      <div className="flag-item-toggle-switch__segment flag-item-toggle-switch__segment--readonly">
        <i className={icon} style={{ ...style, color: activeColor }} aria-hidden="true" />
      </div>
    )
  }

  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className="flag-item-toggle-switch dome-d-flex"
    >
      {SWITCH_ICONS.map(({ value, icon, activeColor, style }) => (
        <div
          key={value}
          aria-hidden="true"
          className={`flag-item-toggle-switch__segment${value === checked ? ' flag-item-toggle-switch__segment--active' : ''}`}
        >
          <i
            className={icon}
            style={{
              ...style,
              color: value === checked ? activeColor : '#B7B7B7',
              transition: 'color 500ms ease-in-out',
            }}
          />
        </div>
      ))}
    </button>
  )
}

const FileName = ({ fileName }) => (
  <span className="dome-p3 dome-color-med-grey dome-overflow-ellipsis flag-item__filename">{fileName}</span>
)

const Content = ({ flag }) => (
  <div className="dome-relative">
    <div className="flag-item__content">
      <span className="flag-item__reason dome-p2 dome-color-darkest-grey" style={{ whiteSpace: 'pre-wrap' }}>
        {flag.reason}
      </span>
    </div>
    <div className="flag-item__overlay-container">
      <div className="flag-item__overlay-content">
        <span className="flag-item__reason dome-p2 dome-color-lite-grey" style={{ whiteSpace: 'pre-wrap' }}>
          {flag.reason}
        </span>
      </div>
    </div>
  </div>
)

const ToggleActions = ({ status, checked, onChange, readOnly, label }) => (
  <div className="dome-relative dome-d-flex dome-justify-end dome-gap6 dome-align-center">
    <span className="flag-item__status dome-p3 dome-text-w500">{status}</span>
    <ToggleSwitch checked={checked} onChange={onChange} readOnly={readOnly} label={label} />
  </div>
)

const EditDeleteActions = ({ onEdit, onDelete }) => (
  <div className="dome-d-flex dome-justify-end dome-gap12 dome-align-center">
    <button onClick={onEdit} className="flag-item__action-btn" aria-label="Edit flag">
      <i className="fa-graphite fa-light fa-pencil dome-color-lite-grey" aria-hidden="true"></i>
    </button>
    <button onClick={onDelete} className="flag-item__action-btn" aria-label="Delete flag">
      <i className="fa-solid fa-trash-can dome-color-lite-grey" aria-hidden="true"></i>
    </button>
  </div>
)

const Root = ({ children, className, gap }) => (
  <div className={clsx('flag-item', className)} style={gap != null ? { '--flag-item-gap': `${gap}px` } : undefined}>
    {children}
  </div>
)

export const FlagItem = {
  Root,
  Content,
  FileName,
  ToggleActions,
  EditDeleteActions,
}

export const ApplicantFlagItem = ({ completed, isCompleting, flag, fileName, onComplete, readOnly, gap }) => {
  const status = isCompleting && completed ? 'completed!' : ''
  const handleToggleComplete = (value) => onComplete?.(value)

  const flagAttributionClassName = clsx(
    'dome-p3 dome-color-med-grey flag-item__contribution',
    completed && 'flag-item__contribution--done'
  )

  return (
    <FlagItem.Root className={clsx(isCompleting && 'flag-item__completing')} gap={gap}>
      <div className="dome-d-flex dome-align-center dome-justify-between">
        <FlagAttribution flag={flag} className={flagAttributionClassName} />
        <FlagItem.ToggleActions
          status={status}
          checked={completed}
          readOnly={readOnly}
          onChange={handleToggleComplete}
          label="Mark as completed"
        />
      </div>
      <FlagItem.Content flag={flag} />
      {fileName && <FlagItem.FileName fileName={fileName} />}
    </FlagItem.Root>
  )
}

export const ProcessorFlagItem = ({
  resolved,
  isDraft,
  readOnly,
  isResolving,
  flag,
  fileName,
  onEdit,
  onDelete,
  onResolve,
  gap,
}) => {
  const status = isResolving && resolved ? 'resolved!' : ''
  const handleEdit = () => onEdit?.(flag)
  const handleDelete = () => onDelete?.(flag)
  const handleToggleComplete = (value) => onResolve?.(value)

  const flagAttributionClassName = clsx(
    'dome-p3 dome-color-med-grey flag-item__contribution',
    resolved && 'flag-item__contribution--done'
  )

  return (
    <FlagItem.Root className={clsx(isResolving && 'flag-item__completing')} gap={gap}>
      <div className="dome-d-flex dome-align-center dome-justify-between">
        <FlagAttribution flag={flag} className={flagAttributionClassName} />
        {isDraft ? (
          <FlagItem.EditDeleteActions onEdit={handleEdit} onDelete={handleDelete} />
        ) : (
          <FlagItem.ToggleActions
            status={status}
            checked={resolved}
            readOnly={readOnly}
            onChange={handleToggleComplete}
            label="Mark as resolved"
          />
        )}
      </div>
      <FlagItem.Content flag={flag} />
      {fileName && <FlagItem.FileName fileName={fileName} />}
    </FlagItem.Root>
  )
}
